import React, { useState } from 'react';
import Modal from 'react-modal';
import { Button, Spinner } from 'reactstrap';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';

const PdfUploadModal = ({
  isOpen,
  onClose,
  onFileSelected,
  noteTypeData,
  minNumberNoteType,
  maxNumberNoteType,
  innerChildren,
  generateTemplate,
  showLoader,
  buttonText,
}) => {
  const [errorMessage, setErrorMessage] = useState('');
  const isSmallScreen = useMediaQuery({ query: '(max-width: 600px)' });

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const remainingCapacity = maxNumberNoteType - noteTypeData.length;

    if (files.length > remainingCapacity) {
      setErrorMessage(`You can only add ${remainingCapacity} file(s).`);
      return;
    }

    setErrorMessage('');
    onFileSelected(files);
  };

  const handleGenerateTemplate = () => {
    if (noteTypeData && noteTypeData.length >= minNumberNoteType) {
      generateTemplate();
      onClose();
    }
  };

  const isButtonEnabled =
    noteTypeData.length >= minNumberNoteType &&
    noteTypeData.length <= maxNumberNoteType;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={customStyles}
      ariaHideApp={false}
    >
      <h2 style={headingStyle}>Upload PDF Files</h2>
      <p style={infoMessageStyle}>
        <i
          className="fa fa-exclamation-circle"
          aria-hidden="true"
          style={{ color: '#f76c6c', marginRight: '4px' }}
        />
        Please upload {minNumberNoteType} to {maxNumberNoteType} PDF files to
        help us create your custom template.
      </p>
      <input
        type="file"
        accept="application/pdf"
        onChange={handleFileChange}
        style={inputStyle}
        multiple // Allow multiple file selection
        disabled={noteTypeData.length === maxNumberNoteType} // Disable when max files are uploaded
      />
      {/* Show message when maximum files have been added */}
      {noteTypeData.length === maxNumberNoteType && (
        <p style={errorStyle}>
          Maximum {maxNumberNoteType} files can be added.
        </p>
      )}
      {errorMessage && <p style={errorStyle}>{errorMessage}</p>}

      {showLoader && (
        <div style={loaderContainerStyle}>
          <Spinner color="primary" />
          <p>Uploading files, please wait...</p>
        </div>
      )}
      <div
        style={{
          ...buttonContainerStyle,
          flexDirection: 'row',
        }}
      >
        <Button
          color="primary"
          size={isSmallScreen ? 'sm' : 'lg'}
          style={primaryButtonStyle}
          onClick={handleGenerateTemplate}
          id="generate-template-button"
          disabled={!isButtonEnabled}
        >
          {buttonText}
        </Button>
        <Button
          color="danger"
          size={isSmallScreen ? 'sm' : 'lg'}
          style={cancelButtonStyle}
          onClick={() => {
            onClose();
          }}
        >
          Cancel
        </Button>
      </div>
      {innerChildren && (
        <div style={exampleNoteStyle}>
          {innerChildren}{' '}
          {/* Renders whatever component is passed as `innerChildren` */}
        </div>
      )}
    </Modal>
  );
};

// Custom styles for the modal
const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    transform: 'translate(-50%, -50%)',
    padding: '30px',
    borderRadius: '10px',
    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)',
    width: '95%',
    maxWidth: '600px',
  },
};

const headingStyle = {
  fontSize: '1.3em',
  fontWeight: '600',
  marginBottom: '15px',
  textAlign: 'center',
  color: '#333',
};

const inputStyle = {
  display: 'block',
  margin: '20px auto',
  padding: '8px',
  width: '90%',
  maxWidth: '500px',
  borderRadius: '8px',
  border: '1px solid #ccc',
};

const errorStyle = {
  color: '#ff4d4f',
  marginBottom: '10px',
  fontSize: '0.75em',
  textAlign: 'center',
};

const infoMessageStyle = {
  fontSize: '0.75em',
  color: '#333',
  textAlign: 'center',
  margin: '0 auto 15px auto',
  backgroundColor: '#FFF3CD',
  padding: '10px',
  borderRadius: '5px',
};

const loaderContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  marginBottom: '15px',
  color: '#333',
};

const buttonContainerStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '10px',
  marginTop: '20px',
};

const primaryButtonStyle = {
  padding: '10px 18px',
  fontSize: '1em',
  borderRadius: '8px',
  minWidth: '150px',
  transition: 'all 0.3s ease',
};

const cancelButtonStyle = {
  padding: '10px 18px',
  fontSize: '1em',
  borderRadius: '8px',
  minWidth: '150px',
  transition: 'all 0.3s ease',
};

const exampleNoteStyle = {
  marginTop: '20px',
  fontSize: '0.9em',
  maxHeight: '250px',
  overflowY: 'auto',
};

// PropTypes validation
PdfUploadModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onFileSelected: PropTypes.func.isRequired,
  noteTypeData: PropTypes.array.isRequired,
  minNumberNoteType: PropTypes.number.isRequired,
  maxNumberNoteType: PropTypes.number.isRequired,
  innerChildren: PropTypes.node,
  generateTemplate: PropTypes.func.isRequired,
  showLoader: PropTypes.bool,
};

export default PdfUploadModal;
